import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { finleyAssert } from '@finley-technologies/common';

import {
  AttributeInputBase,
  DynamicAttributeFormValue,
} from 'packages/common-frontend/components/AttributeInput/AttributeInputBase';
import {
  mapGqlAttributeToAttribute,
  attributeValuePropertyLookup,
  processFormValue,
} from 'packages/common-frontend/components/AttributeInput/utils';
import { TextInput } from 'packages/common-frontend/components/InputsBETA/TextInput';
import {
  useCreateLimitedPartnerMutation,
  useGetLimitedPartnerAttributeListQuery,
} from 'packages/limitedPartners/requests/requests.gql.generated';
import { ErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import {
  successToastConfig,
  errorToastConfig,
} from '~/packages/common-frontend/constants';

type AddLimitedPartnerButtonProps = {
  onAddLimitedPartner: (params: { lpId: string; lpName: string }) => void;
};

type DynamicAttributeForm = {
  attributes: Record<string, DynamicAttributeFormValue>;
  limitedPartnerName: string;
};

const AddLimitedPartnerButtonBase = ({
  onAddLimitedPartner,
}: AddLimitedPartnerButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();

  const { user } = useUserAuthOrThrow();

  const form = useForm<DynamicAttributeForm>();

  const {
    data: attributesData,
    loading,
    error,
  } = useGetLimitedPartnerAttributeListQuery({
    variables: {
      organizationId: user.organizationId,
    },
  });

  const attributes = useMemo(
    () =>
      attributesData?.attributes.map((attr) =>
        mapGqlAttributeToAttribute(attr),
      ) ?? [],
    [attributesData],
  );

  useEffect(() => {
    // Reset everything when the modal is opened
    if (isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  const [createLimitedPartner] = useCreateLimitedPartnerMutation({
    onCompleted: (data) => {
      if (data && data.createLimitedPartner) {
        const { limitedPartnerName } = form.getValues();

        successToastConfig.id = `${data.createLimitedPartner.id}-limited-partner-toast`;
        successToastConfig.title = `Limited Partner ${limitedPartnerName} created!`;
        toast(successToastConfig);
        onAddLimitedPartner({
          lpId: data.createLimitedPartner.id,
          lpName: limitedPartnerName,
        });
      }
    },
    onError: (err) => {
      errorToastConfig.title = err.message;
      toast(errorToastConfig);
    },
  });

  if (error) {
    throw error;
  }

  if (loading || !attributesData) {
    return null;
  }

  const onSubmit = (formData: DynamicAttributeForm) => {
    const { limitedPartnerName } = formData;

    const attributeValues = Object.entries(formData.attributes)
      .map(([attributeId, newValue]) => {
        const attr = attributes.find((a) => a.attributeId === attributeId);

        finleyAssert(
          attr,
          `Attribute with id ${attributeId} not found in attributes list`,
        );

        // its possible that the attribute value was not filled in.
        if (newValue === undefined) {
          // we filter out these before passing attributes to the mutation
          return null;
        }

        return {
          attributeId,
          type: attr.type,
          [attributeValuePropertyLookup[attr.type]]: processFormValue(
            newValue,
            attr.type,
          ),
        };
      })
      .filter((a) => a !== null);

    createLimitedPartner({
      variables: {
        name: limitedPartnerName,
        lenderOrganizationId: user.organizationId,
        attributeValues,
      },
    });
    onClose();
  };

  return (
    <>
      <Button
        variant="link"
        colorScheme="blue"
        fontSize="sm"
        onClick={onOpen}
        textDecoration="none"
      >
        Add LP
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <ModalHeader p="40px 40px 16px">Add LP</ModalHeader>
            <ModalCloseButton />
            <ModalBody
              display="flex"
              flexDirection="column"
              gap="1rem"
              p="8px 40px"
            >
              <FormProvider {...form}>
                <TextInput<DynamicAttributeForm>
                  name="limitedPartnerName"
                  label="Name"
                  placeholder="Enter limited partner name"
                  required
                />

                {attributes.map((attribute) => (
                  <AttributeInputBase<DynamicAttributeForm>
                    key={attribute.attributeId}
                    attribute={attribute}
                    name={`attributes.${attribute.attributeId}`}
                  />
                ))}
              </FormProvider>
            </ModalBody>

            <ModalFooter p="16px 40px 40px">
              <Button
                variant="secondary"
                colorScheme="v2-aurora"
                mr={3}
                onClick={onClose}
              >
                Cancel
              </Button>

              <Button colorScheme="v2-aurora" type="submit">
                Add
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
};

export const AddLimitedPartnerButton = ({
  onAddLimitedPartner,
}: AddLimitedPartnerButtonProps) => (
  <ErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <></>;
    }}
  >
    <AddLimitedPartnerButtonBase onAddLimitedPartner={onAddLimitedPartner} />
  </ErrorBoundary>
);
